import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoungeLogo } from './LoungeLogo';
import { StaffOnlyTimeRangeSelector } from './StaffOnlyTimeRangeSelector';
import type { AirportFlightType } from '../../hooks/useAreas';
import type { UseFormRegister, UseFormSetValue } from 'react-hook-form';

export interface StaffOnlyFormData {
  spaceType: string;
  airport: string;
  flightType: string;
  terminal: string;
  lounge: string;
  areaIds: number[];
  time: string;
  searchDate: string;
  startsAtHour: string;
  startsAtMinute: string;
  endsAtHour: string;
  endsAtMinute: string;
  reservationNumberSearch: string;
  customerNameSearch: string;
}

interface StaffOnlyFilterFormProps {
  register: UseFormRegister<StaffOnlyFormData>;
  setValue: UseFormSetValue<StaffOnlyFormData>;
  airports: AirportFlightType[];
  terminals: string[];
  lounges: string[];
  isLoadingAirports: boolean;
  isLoadingTerminals: boolean;
  isLoadingLounges: boolean;
  selectedAirport: string;
  selectedFlightType: string;
  selectedTerminal: string;
  selectedLounge: string;
  selectedSpaceType: string;
  selectedDate: string;
  startsAtHour: string;
  startsAtMinute: string;
  endsAtHour: string;
  endsAtMinute: string;
  defaultTimeExpanded?: boolean;
}

export const StaffOnlyFilterForm = ({
  register,
  setValue,
  airports,
  terminals,
  lounges,
  isLoadingAirports,
  isLoadingTerminals,
  isLoadingLounges,
  selectedAirport,
  selectedFlightType,
  selectedTerminal,
  selectedLounge,
  selectedSpaceType,
  selectedDate,
  startsAtHour,
  startsAtMinute,
  endsAtHour,
  endsAtMinute,
  defaultTimeExpanded,
}: StaffOnlyFilterFormProps) => {
  const { t } = useTranslation();

  // 各フィールドの開閉状態
  const [isAirportOpen, setIsAirportOpen] = useState(false);
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);
  const [isLoungeOpen, setIsLoungeOpen] = useState(false);

  const isDisabled = !selectedSpaceType;

  // 空港選択時のハンドラー
  const handleAirportSelect = (airport: string, flightType: string) => {
    setValue('airport', airport);
    setValue('flightType', flightType);
    setIsAirportOpen(false);
  };

  // ターミナル選択時のハンドラー
  const handleTerminalSelect = (terminal: string) => {
    setValue('terminal', terminal);
    setIsTerminalOpen(false);
  };

  // ラウンジ選択時のハンドラー
  const handleLoungeSelect = (lounge: string) => {
    setValue('lounge', lounge);
    setIsLoungeOpen(false);
  };

  // 選択されているかどうかの判定
  const hasAirportSelected = !!selectedAirport && !!selectedFlightType;
  const hasTerminalSelected = !!selectedTerminal;
  const hasLoungeSelected = !!selectedLounge;

  return (
    <div
      className={`e__fields e-search__row e-search__row--grid e-search__row--lockable ${isDisabled ? 'is-disabled' : ''}`}
    >
      {/* 空港 */}
      <div
        className={`e__field e-search__field e-search__field--airport ${isAirportOpen ? 'is-open' : ''}`}
      >
        <div
          className={`e-search__field-header ${hasAirportSelected ? 'is-filled' : ''}`}
          onClick={() => setIsAirportOpen(!isAirportOpen)}
        >
          <div className="e-search__field-label">
            {hasAirportSelected
              ? t(`common.airport.${selectedAirport}`)
              : t('staffOnly.filters.selectAirport')}
          </div>
        </div>
        <div className="e-search__field-body">
          <div className="e-dropdown e-dropdown--airport">
            <div className="e-dropdown__menu">
              {isLoadingAirports ? (
                <div className="e-dropdown__item">{t('common.loading')}</div>
              ) : (
                airports.map((item) => (
                  <button
                    key={`${item.airport}|${item.flightType}`}
                    type="button"
                    className="e-dropdown__item"
                    onClick={() =>
                      handleAirportSelect(item.airport, item.flightType)
                    }
                  >
                    {t(`common.airport.${item.airport}`)}
                  </button>
                ))
              )}
            </div>
            <input type="hidden" {...register('airport')} />
            <input type="hidden" {...register('flightType')} />
          </div>
        </div>
      </div>

      {/* ターミナル/サテライト */}
      <div
        className={`e__field e-search__field e-search__field--terminal ${isTerminalOpen ? 'is-open' : ''}`}
      >
        <div
          className={`e-search__field-header ${hasTerminalSelected ? 'is-filled' : ''}`}
          onClick={() => setIsTerminalOpen(!isTerminalOpen)}
        >
          <div className="e-search__field-label">
            {hasTerminalSelected
              ? t(`common.terminal.${selectedTerminal}`)
              : t('staffOnly.filters.selectTerminal')}
          </div>
        </div>
        <div className="e-search__field-body">
          <div className="e-dropdown e-dropdown--terminal">
            <div className="e-dropdown__menu">
              {isLoadingTerminals ? (
                <div className="e-dropdown__item">{t('common.loading')}</div>
              ) : (
                terminals.map((terminal) => (
                  <button
                    key={terminal}
                    type="button"
                    className="e-dropdown__item"
                    onClick={() => handleTerminalSelect(terminal)}
                  >
                    {t(`common.terminal.${terminal}`)}
                  </button>
                ))
              )}
            </div>
            <input type="hidden" {...register('terminal')} />
          </div>
        </div>
      </div>

      {/* ラウンジ */}
      <div
        className={`e__field e-search__field e-search__field--lounge ${isLoungeOpen ? 'is-open' : ''}`}
      >
        <div
          className={`e-search__field-header ${hasLoungeSelected ? 'is-filled' : ''}`}
          onClick={() => setIsLoungeOpen(!isLoungeOpen)}
        >
          <div className="e-search__field-label">
            {hasLoungeSelected ? (
              <LoungeLogo
                lounge={selectedLounge}
                className="reservation-card__lounge-img"
                altText={t(`common.lounge.${selectedLounge}`)}
              />
            ) : (
              t('staffOnly.filters.selectLounge')
            )}
          </div>
        </div>
        <div className="e-search__field-body">
          <div className="e-dropdown e-dropdown--lounge">
            <div className="e-dropdown__menu">
              {isLoadingLounges ? (
                <div className="e-dropdown__item">{t('common.loading')}</div>
              ) : (
                lounges.map((lounge) => (
                  <button
                    key={lounge}
                    type="button"
                    className="e-dropdown__item"
                    onClick={() => handleLoungeSelect(lounge)}
                  >
                    <LoungeLogo
                      lounge={lounge}
                      className="reservation-card__lounge-img"
                      altText={t(`common.lounge.${lounge}`)}
                    />
                  </button>
                ))
              )}
            </div>
            <input type="hidden" {...register('lounge')} />
          </div>
        </div>
      </div>

      {/* 利用日時 */}
      <StaffOnlyTimeRangeSelector
        register={register}
        setValue={setValue}
        selectedSpaceType={selectedSpaceType}
        selectedDate={selectedDate}
        startsAtHour={startsAtHour}
        startsAtMinute={startsAtMinute}
        endsAtHour={endsAtHour}
        endsAtMinute={endsAtMinute}
        defaultExpanded={defaultTimeExpanded}
      />
    </div>
  );
};
